/* eslint-disable unicorn/prefer-spread */
import { associateResponseWithForm } from '~/framework/utils/forms';
import { type App } from '~/app';

import { scrollIntoView } from '../../../../framework/scripts/utils/scroll';
import { selectDefaultInButtonGroup } from '../fields/button-grouping/button-grouping';
import { selectDefaultInSelectElement } from '../fields/select/select';
import { destroyFormRecaptcha,initialiseFormRecaptcha,resetForm, resetFormInputElements, resetValidations  } from '../forms/form';

import { type Event } from './interfaces';

declare const window: Window & { dataLayer: Array<Record<string, unknown>> };


export default (app: App) => {
    app.logger.debug('[Stockland FE] Initialise Stockland Modal Form Component');

    const listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]> = [];

    app.afterMount
        .then(() => {
            mountModalForm(listeners);
        })
        .catch((error) => {
            throw new Error('[Stockland FE] Unable to register Stockland Modal Form Component', error);
        });

    // eslint-disable-next-line @typescript-eslint/naming-convention
    return function destroyModalForm() {
        unMountModalForm(listeners);

        app.logger.debug('[DXA] Modal Form Component destroyed');
    };
};

// eslint-disable-next-line @typescript-eslint/naming-convention
export function mountModalForm(
    listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]>,
    root: HTMLElement | null = null
) {
    let rootNode: Document | HTMLElement = document;
    if (root) rootNode = root;

    const modalButtons = rootNode.querySelectorAll<HTMLElement>('.modal-form-button');
    const form = rootNode.querySelector<HTMLElement>('.modal-dialog .form-container form.EPiServerForms'); // Looks for the first instance of a form within the modal

    if (
        form
    ) {
        const target = form.querySelector<HTMLElement>('.Form__Status .Form__Status__Message');
        if (target) {
            const observer = new MutationObserver(() => {
                const modal = form.closest<HTMLElement>('.modal');
                if(modal)
                {
                    const successElement = modal.querySelector<HTMLAnchorElement>('.Form__Status__Message.Form__Success__Message');
                    if(successElement)
                    {
                        form.dataset.formSubmitted='true';
                    }

                    if(	form.dataset.formType == 'RemindMe' && // requires the "Display message after form submission" in the CMS to have an anchor
                        modal?.dataset?.formCalendar) {
                        const icsUrl = generateIcsUrl(modal.dataset.formCalendar);
                        const anchor = modal.querySelector<HTMLAnchorElement>('.Form__Status__Message.Form__Success__Message a');
                        if (anchor) {
                            anchor.href = icsUrl;
                        }
                    }

                }
            });
            const config = { attributes: true, childList: true, characterData: true };
            observer.observe(target, config);
        }
    }

    for (const modalButton of modalButtons) {
        let modalButtonId = modalButton.dataset.bsTarget;
        if(modalButton.classList?.contains('mimic-enquiry-button')) {
            //find the existing enquiry button and attach the form associated with it
            const mainEnquiryButton = Array.from(modalButtons).find(x=>x.dataset.bsTarget);
            if(mainEnquiryButton){
                modalButtonId = mainEnquiryButton.dataset.bsTarget;
                modalButton.dataset.bsTarget = modalButtonId;
            }

            modalButton.addEventListener('click', (event) => {
                scrollToTakeoverCallout(event);
            });
        }

        const modal = modalButtonId ? rootNode.querySelector<HTMLElement>(`${modalButtonId}`) : null;

        if (modalButton && modal && form) {
            modal.addEventListener('show.bs.modal', (event) => initialiseFormInModal(form, modal, event));
            listeners.push([modal, 'show.bs.modal', (event) => initialiseFormInModal(form, modal, event)]);

            modal.addEventListener('hidden.bs.modal', () => destroyFormInModal(form, modal));
            listeners.push([modal, 'hidden.bs.modal', () => destroyFormInModal(form, modal)]);
        }
    }
}

const scrollToTakeoverCallout = (event: globalThis.Event) => {
    const takeoverCallout = document.querySelector<HTMLElement>('.takeover-callout');
    const takeoverCalloutForm = document.querySelector<HTMLElement>('.takeover-callout-form-container form');
    if (takeoverCallout && takeoverCalloutForm ) {
        scrollIntoView(takeoverCallout);
        return event.preventDefault();
    }
}

function unMountModalForm(listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]>) {
    for (const [element, event, listener] of listeners) {
        element.removeEventListener(event, listener);
    }
}

const initialiseFormInModal = (form: HTMLElement, modal: HTMLElement, event: globalThis.Event) => {
    // Check if there is an inline form on the page, and if there is, we scroll to it and don't open the modal (If it's the same form then it should have been removed from the modal by the takeover callout anyway)
    scrollToTakeoverCallout(event);

    resetForm(form);
    resetValidations(form);
    const modalId = modal?.id;
    const modalEventId = modal.dataset.eventId;

    // Check if the modal already has the form before moving it
    const formInModal = modal.querySelector<HTMLElement>('.form-container form.EPiServerForms');
    if (!formInModal) {
        // Move the remind me form on the page into the modal you are displaying
        const formContainer = modal.querySelector<HTMLElement>('.form-container');
        formContainer?.append(form);
    }

    // Update hidden input with event id
    if (modalEventId){
        associateResponseWithForm(`#${modalId}`, modalEventId, '###EventIdToken###');
    }

    //check if any default value
    const defaultSelectionElements = form.querySelectorAll<HTMLElement>('[data-default]');
    if(defaultSelectionElements && defaultSelectionElements.length>0){
        for(const currentElement of defaultSelectionElements)
        {
            if(currentElement.dataset.default && currentElement.classList.contains('button-grouping-hidden-element'))
            {
                selectDefaultInButtonGroup(currentElement,currentElement.dataset.default) 
            }
            else if(currentElement.dataset.default && currentElement.classList.contains('select-input-field'))
            {
                setTimeout(() => {
                    selectDefaultInSelectElement(currentElement,currentElement.dataset.default??"");
                }, 1000);
            }
        }
    }

    initialiseFormRecaptcha(form, modalId);
};

const destroyFormInModal = (form: HTMLElement, modal: HTMLElement) => {
    if(!form.dataset.formSubmitted || form.dataset.formSubmitted!='true'){
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(
            {
                "formName":"",
                "formEvent": "modal-close",
                "formSubmitted": false
            }
        );
    }

    resetForm(form);
    resetFormInputElements(form);
    resetValidations(form);
    // Reset hidden input
    const modalId = modal?.id;
    const modalEventId = modal.dataset.eventId;
    if (modalEventId){
        associateResponseWithForm(`#${modalId}`, '###EventIdToken###', modalEventId);
    }

    destroyFormRecaptcha(modal)
}

export const getMainEnquiryForm = () => {
    const modalButton = document.querySelector<HTMLElement>('.enquiry-button-parent .modal-form-button .btn');
    return modalButton;
};

const generateIcsUrl = (json: string) => {
    const eventData = JSON.parse(json) as Event;
    
    // Create ics event
    const eventStartTime = eventData.startTime ? new Date(Number(eventData.startTime) * 1000).toISOString().split("-").join("").split(":").join("").slice(0,15).concat("Z") : '';
    const eventEndTime = eventData.endTime ? new Date(Number(eventData.endTime) * 1000).toISOString().split("-").join("").split(":").join("").slice(0,15).concat("Z")  : '';
    const eventLocation = eventData.location ?? '';
    const eventHeading = eventData.heading ?? '';
    const eventDescription = eventData.description ? eventData.description.replaceAll(/\r\n|\r|\n/g, '<br>') : '';

    const icsMessage = `BEGIN:VCALENDAR\nVERSION:2.0\nMETHOD:PUBLISH\nCALSCALE:GREGORIAN\nBEGIN:VEVENT\nSEQUENCE:0\nCLASS:PUBLIC\nPRIORITY:5\nDTSTAMP:${Date.now()}\nDTSTART:${eventStartTime}\nDTEND:${eventEndTime}\nSUMMARY:${eventHeading}\nLOCATION:${eventLocation}\nDESCRIPTION:${eventDescription}\nX-ALT-DESC;FMTTYPE=text/html:${eventDescription}\nBEGIN:VALARM\nTRIGGER:-PT30M\nACTION:DISPLAY\nDESCRIPTION:Reminder\nEND:VALARM\nEND:VEVENT\nEND:VCALENDAR`;

    return 'data:text/calendar;charset=utf8,' + encodeURIComponent(icsMessage);
};
